import { array, option } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { getAppConfiguration, sendGraphQLRequest } from '@mews-commander/data-access';
import { GuestTotalStaysByIdsResultQuery } from '@mews-commander/guest-profile';
import { keyBy } from '@mews/fp';
import { LoggerAdapter } from '@mews/framework/staging/services/logging/types';
import { getCommonParams } from '../../../../modules/api/selectors';
import { GuestTotalStaysByIds } from './useGuestTotalStaysByIds';

const transformTotalStaysData = ({ guestSummaryByIds }: GuestTotalStaysByIdsResultQuery) =>
    pipe(
        guestSummaryByIds,
        array.filterMap((guestSummary) =>
            !!guestSummary && !!guestSummary?.analytics?.totalStays ? option.some(guestSummary) : option.none
        ),
        (guestSummaries) => (array.isNonEmpty(guestSummaries) ? keyBy('guestId', guestSummaries) : null)
    );

/**
 *
 * Only use in legacy redux based code, otherwise use `useGuestTotalStaysByIds`.
 * @param guestIds
 * @param timeoutInMilliseconds
 */
export const fetchGuestsTotalStaysByIds = async (
    guestIds: string[],
    loggerService: LoggerAdapter,
    timeoutInMilliseconds = 5000
) => {
    const { guestProfileApiUrl } = getAppConfiguration();
    const commonParams = getCommonParams();
    const startTime = new Date();

    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), timeoutInMilliseconds);

    try {
        const result = await sendGraphQLRequest(
            guestProfileApiUrl,
            {
                document: GuestTotalStaysByIds,
                variables: { guestIds },
                signal: controller.signal,
            },
            commonParams
        );
        return transformTotalStaysData(result);
    } catch (e) {
        const endTime = new Date();
        if (e.name === 'TimeoutError' || e.name === 'AbortError') {
            loggerService.error('guest-insights-timeline', 'Request timed out', {
                guestCount: guestIds.length,
                enterpriseId: commonParams.enterpriseId,
                errorMessage: e.message,
                startTimestamp: startTime.toISOString(),
                timeoutTimestamp: endTime.toISOString(),
                elapsedTimeMs: endTime.getTime() - startTime.getTime(),
            });
            return null;
        }
    } finally {
        window.clearTimeout(timeout);
    }
};
